import React, { useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { Portal } from '../helpers/Portal';
const Wrapper = styled.div `
  position: relative;
  max-width: fit-content;
`;
const TooltipWrapper = styled.div `
  background-color: ${({ theme }) => theme.secondary.blendToForeground(1, 950)};
  backdrop-filter: blur(4px);
  color: ${({ theme }) => theme.background};
  border-radius: ${({ theme }) => theme.style.tooltipBorderRadius};
  padding: ${({ theme }) => theme.style.tooltipPadding};
  font-size: ${({ theme }) => theme.style.tooltipFontSize};
  line-height: ${({ theme }) => theme.style.tooltipLineHeight};
  font-weight: ${({ theme }) => theme.style.tooltipFontWeight};
  width: ${({ defaultWidth }) => defaultWidth};
  max-width: ${({ maxWidth }) => maxWidth};
  pointer-events: none;
  user-select: none;
  transition:
    opacity 0.2s,
    transform 0.2s;
  z-index: 5;
  display: flex;
  flex-direction: column;
  gap: 4px;
  filter: drop-shadow(0px 16px 32px rgba(0, 0, 0, 0.15));

  &:before {
    content: '';
    width: 18px;
    height: 8px;
    position: absolute;
    background-color: inherit;
    backdrop-filter: blur(4px);
    mask-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 18 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0.515625 0H17.4862L11.8293 5.65685C10.2672 7.21895 7.73458 7.21895 6.17248 5.65685L0.515625 0Z' fill='black'/%3E%3C/svg%3E");
  }

  small {
    display: block;
    font-size: ${({ theme }) => theme.style.tooltipDescriptionFontSize};
    font-weight: normal;
    color: ${({ theme }) => theme.secondary.blendToBackground(400)};
  }

  ${({ isOpen }) => !isOpen &&
    css `
      opacity: 0;
    `}

  ${({ placement }) => placement.includes('left') &&
    css `
      transform: translate(calc(-4px + ${({ offsetX }) => `${offsetX}px`}), ${({ offsetY }) => `${offsetY}px`});

      &:before {
        left: 100%;
        top: 50%;
        transform: translate(-5px, -50%) rotate(-90deg) translateZ(0);
      }

      ${({ isOpen }) => isOpen &&
        css `
          transform: translate(calc(-8px + ${({ offsetX }) => `${offsetX}px`}), ${({ offsetY }) => `${offsetY}px`});
        `}
    `}

  ${({ placement }) => placement.includes('top') &&
    css `
      transform: translate(${({ offsetX }) => `${offsetX}px`}, calc(-4px + ${({ offsetY }) => `${offsetY}px`}));

      &:before {
        left: 50%;
        top: 100%;
        transform: translate(calc(-50% + ${({ offsetArrowX }) => `${offsetArrowX}px`}), 0) rotate(0) translateZ(0);
      }

      ${({ isOpen }) => isOpen &&
        css `
          transform: translate(${({ offsetX }) => `${offsetX}px`}, calc(-8px + ${({ offsetY }) => `${offsetY}px`}));
        `}
    `}

    ${({ placement }) => placement.includes('top-start') &&
    css `
      transform: translate(${({ offsetX }) => `${offsetX}px`}, calc(-4px + ${({ offsetY }) => `${offsetY}px`}));

      &:before {
        left: ${({ theme }) => theme.style.tooltipPadding};
        top: 100%;
        transform: translate(calc(${({ offsetArrowX }) => `${offsetArrowX}px`}), 0) rotate(0) translateZ(0);
      }

      ${({ isOpen }) => isOpen &&
        css `
          transform: translate(${({ offsetX }) => `${offsetX}px`}, calc(-8px + ${({ offsetY }) => `${offsetY}px`}));
        `}
    `}

    ${({ placement }) => placement.includes('top-end') &&
    css `
      transform: translate(${({ offsetX }) => `${offsetX}px`}, calc(-4px + ${({ offsetY }) => `${offsetY}px`}));

      &:before {
        right: ${({ theme }) => theme.style.tooltipPadding};
        left: auto;
        top: 100%;
        transform: translate(calc(${({ offsetArrowX }) => `${offsetArrowX}px`}), 0) rotate(0) translateZ(0);
      }

      ${({ isOpen }) => isOpen &&
        css `
          transform: translate(${({ offsetX }) => `${offsetX}px`}, calc(-8px + ${({ offsetY }) => `${offsetY}px`}));
        `}
    `}

  ${({ placement }) => placement.includes('right') &&
    css `
      transform: translate(calc(4px + ${({ offsetX }) => `${offsetX}px`}), ${({ offsetY }) => `${offsetY}px`});

      &:before {
        right: 100%;
        top: 50%;
        transform: translate(5px, -50%) rotate(90deg) translateZ(0);
      }

      ${({ isOpen }) => isOpen &&
        css `
          transform: translate(calc(8px + ${({ offsetX }) => `${offsetX}px`}), ${({ offsetY }) => `${offsetY}px`});
        `}
    `}

  ${({ placement }) => placement.includes('bottom') &&
    css `
      transform: translate(${({ offsetX }) => `${offsetX}px`}, calc(4px + ${({ offsetY }) => `${offsetY}px`}));

      &:before {
        left: 50%;
        bottom: 100%;
        transform: translate(calc(-50% + ${({ offsetArrowX }) => `${offsetArrowX}px`}), 0) rotate(180deg) translateZ(0);
      }

      ${({ isOpen }) => isOpen &&
        css `
          transform: translate(${({ offsetX }) => `${offsetX}px`}, calc(8px + ${({ offsetY }) => `${offsetY}px`}));
        `}
    `}
`;
export const Tooltip = (props) => {
    const wrapperRef = useRef(null);
    const intervalRef = useRef(null);
    const portalRef = useRef(null);
    const [isOpenState, setIsOpenState] = useState(props.isOpen ?? false);
    const styleProps = {
        isOpen: props.isOpen ?? isOpenState,
        placement: props.placement ?? 'top',
        defaultWidth: props.defaultWidth ?? 'max-content',
        maxWidth: props.maxWidth ?? '220px',
        offsetX: props.offsetX ?? 0,
        offsetY: props.offsetY ?? 0,
        offsetArrowX: props.offsetArrowX ?? 0,
    };
    const handleShow = (value) => {
        if (!value) {
            clearTimeout(intervalRef.current);
            intervalRef.current = null;
            setIsOpenState(false);
            return;
        }
        if (intervalRef.current) {
            return;
        }
        portalRef.current?.update();
        intervalRef.current = setTimeout(() => setIsOpenState(true), props.delay ?? 0);
    };
    const handleHoverChange = (value) => {
        if (props.triggerClick) {
            return;
        }
        handleShow(value);
    };
    const handleClick = () => {
        if (!props.triggerClick) {
            return;
        }
        handleShow(!isOpenState);
    };
    const handleDocumentClick = () => {
        if (!props.triggerClick || !isOpenState) {
            return;
        }
        setIsOpenState(false);
    };
    useEffect(() => {
        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(Wrapper, { onPointerEnter: () => handleHoverChange(true), onPointerMove: () => handleHoverChange(true), onPointerLeave: () => handleHoverChange(false), onClick: handleClick, ref: wrapperRef, className: props.className }, props.children),
        React.createElement(Portal, { reference: wrapperRef, placement: styleProps.placement, restrictFlipCross: true, restrictFlipMain: true, pointerEventsNone: true, zIndex: props.zIndex, ref: portalRef },
            React.createElement(TooltipWrapper, { ...styleProps }, props.content))));
};
//# sourceMappingURL=Tooltip.js.map